import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { getFormSectionStyles } from "./formSectionStyles";
import WizardModuleSelector from "./WizardModuleSelector";
import IframeOrderingTab from "./IframeOrderingTab";

interface ClientProcessConfig {
  [key: string]: any;
}

interface ClientProcessFormProps {
  config: ClientProcessConfig;
  handleChange: (path: string, value: any) => void;
  /** When true, render only the tabs/fields a standalone mobile phone uses. */
  isMobile?: boolean;
  /** Sibling assetSyndication config — powers the Iframe tab's "Copy from
   *  syndication" action. */
  syndicationConfig?: Record<string, any>;
}

interface FieldGroup {
  /** Optional eyebrow label shown above the group. Omit for an ungrouped block. */
  title?: string;
  /** Optional one-line helper rendered under the title. */
  description?: string;
  fields: string[];
}

const tabSections = [
  "Settings",
  "Phone Sync",
  "Auto Scan",
  "Leg Fold",
  "Phone Camera",
  "Preferences",
  "Wizard",
  "Overlay Order",
  "Iframe",
];

/** Visual grouping of fields per tab. Each group renders with its own eyebrow
 *  label and renders fields with shared spacing. Switch fields and scalar
 *  fields can mix freely inside a group — the renderer picks the right control
 *  based on the value's type. */
const tabLayouts: Record<string, FieldGroup[]> = {
  Settings: [
    {
      title: "Environment & URLs",
      fields: ["url", "name", "s3_bucket", "ums_environment", "location_id", "unique_id"],
    },
    {
      title: "VIN configuration",
      fields: ["vin_length", "vin_regex", "prefix_removal"],
    },
    {
      title: "Feedback terminology",
      description: "Strings shown to operators during scan failure / retry flows.",
      fields: [
        "vin_scan_terminology",
        "vin_feedback_terminology",
        "vin_length_err_terminology",
      ],
    },
    {
      title: "Feature toggles",
      fields: ["is_default", "is_display_tablet_rooftop", "use_lighting_controller"],
    },
  ],
  "Phone Sync": [
    {
      fields: ["phone_sync_terminology"],
    },
    {
      title: "Toggles",
      fields: ["phone_sync", "phone_leg_control", "phone_uc_upload_delay"],
    },
  ],
  "Auto Scan": [
    {
      title: "Timing",
      fields: [
        "auto_scan_countdown_length",
        "manual_scan_countdown_length",
        "display_screen_sleep_timer",
      ],
    },
    {
      title: "Behaviour toggles",
      fields: [
        "auto_scan_enabled",
        "auto_scan_pano",
        "blackout_screen_during_capture",
        "tablet_rooftop_select",
        "skip_interior_simultaneous",
      ],
    },
  ],
  "Leg Fold": [
    {
      description: "Timing controls for actuator leg fold and stagger.",
      fields: [
        "actuator_fold_trigger_distance",
        "actuator_leg_stagger_time",
        "actuator_trigger_count_threshold",
        "actuator_leg_cycle_time",
      ],
    },
  ],
  "Phone Camera": [
    {
      fields: ["phone_camera_resolution", "phone_camera_lens"],
    },
  ],
  Preferences: [
    {
      title: "Behaviour",
      fields: [
        "use_nhtsa_database",
        "allow_cancel_vehicle",
        "dual_hand_scan_capture",
        "preferences.skipExternal",
        "preferences.skipPano",
      ],
    },
    {
      title: "VIN preferences",
      fields: ["preferences.vinchar", "preferences.vinBypass"],
    },
  ],
  Wizard: [
    {
      description: "Wizard step lists. Editable as JSON.",
      fields: ["enabled", "disabled"],
    },
  ],
  "Overlay Order": [
    {
      description: "Overlay sequence used by the carousel.",
      fields: ["overlays", "overlay"],
    },
  ],
};

// Mobile-phone systems only expose the tabs that map to real phone behaviour.
// No Phone Sync (that's phone<->odroid), Auto Scan or Leg Fold (rig hardware).
const mobileTabSections = [
  "Settings",
  "Phone Camera",
  "Preferences",
  "Wizard",
  "Overlay Order",
  "Iframe",
];

const mobileTabLayouts: Record<string, FieldGroup[]> = {
  Settings: [
    {
      title: "Environment & URLs",
      fields: ["url", "name", "s3_bucket", "ums_environment", "location_id"],
    },
    {
      title: "VIN configuration",
      fields: ["vin_length", "vin_regex", "prefix_removal"],
    },
    {
      title: "Feedback terminology",
      description: "Strings shown to operators during scan failure / retry flows.",
      fields: [
        "vin_scan_terminology",
        "vin_feedback_terminology",
        "vin_length_err_terminology",
        "regex_rejection_message",
      ],
    },
    {
      title: "Feature toggles",
      fields: ["is_default"],
    },
  ],
  "Phone Camera": [
    {
      fields: [
        "phone_camera_resolution",
        "phone_camera_lens",
        "phone_camera_lens_alt",
        "manual_scan_countdown_length",
      ],
    },
    {
      title: "Manual capture",
      fields: ["allow_portrait_manuals"],
    },
  ],
  Preferences: [
    {
      title: "Behaviour",
      fields: ["use_nhtsa_database", "allow_cancel_vehicle"],
    },
    {
      title: "VIN preferences",
      fields: ["preferences.vinchar", "preferences.vinBypass"],
    },
  ],
  Wizard: [
    {
      description: "Wizard step lists. Editable as JSON.",
      fields: ["enabled", "disabled"],
    },
  ],
  "Overlay Order": [
    {
      description: "Overlay sequence used by the carousel.",
      fields: ["overlays", "overlay"],
    },
  ],
};

const TAB_DESCRIPTIONS: Record<string, string> = {
  Settings: "Core configuration values that identify this config and shape its display behaviour.",
  "Phone Sync": "How the mobile capture device communicates with this configuration.",
  "Auto Scan": "Timing and behaviour for the automated scanning flow.",
  "Leg Fold": "Actuator timing parameters that drive the unit's leg fold sequence.",
  "Phone Camera": "Phone-camera hardware preferences applied during capture.",
  Preferences: "Behavioural preferences and VIN handling rules.",
  Wizard: "Steps enabled or disabled in the operator wizard flow.",
  "Overlay Order": "Display order of overlays used downstream of the configuration.",
  Iframe: "Image order the public iframe uses when no image type is in the URL.",
};

const ACRONYMS = new Set([
  "VIN",
  "URL",
  "S3",
  "API",
  "UMS",
  "NHTSA",
  "ID",
  "IP",
]);

const NUMERIC_FIELDS = new Set([
  "location_id",
  "vin_length",
  "prefix_removal",
  "auto_scan_countdown_length",
  "manual_scan_countdown_length",
  "display_screen_sleep_timer",
  "actuator_fold_trigger_distance",
  "actuator_leg_stagger_time",
  "actuator_trigger_count_threshold",
  "actuator_leg_cycle_time",
]);

// Fields that are visible but not user-editable here. The actual value is
// owned somewhere else (e.g. the system-level record or the device's ENV var).
const READ_ONLY_FIELDS: Record<string, string> = {
  ums_environment: "Sourced from the system-level environment and the device's ENV var. Not editable per config.",
};

/** Turn "vin_scan_terminology" -> "VIN Scan Terminology" and respect known acronyms. */
const humanizeFieldKey = (fieldKey: string) => {
  const last = fieldKey.split(".").pop() ?? fieldKey;
  return last
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((word) => {
      const upper = word.toUpperCase();
      if (ACRONYMS.has(upper)) return upper;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

const readPath = (config: ClientProcessConfig, fieldKey: string) =>
  fieldKey.split(".").reduce<any>((obj, k) => obj?.[k], config);

const isNumericField = (key: string, value: any) =>
  NUMERIC_FIELDS.has(key) || typeof value === "number";

/** JSON editor for nested object/array fields (e.g. Overlay Order). Keeps a
 *  local text buffer so every keystroke sticks, and only commits to the config
 *  when the text parses as valid JSON — the previous inline editor discarded
 *  keystrokes whenever the intermediate text was invalid, which "broke" editing. */
const JsonObjectField: React.FC<{
  label: string;
  value: any;
  onCommit: (parsed: any) => void;
  sx?: any;
}> = ({ label, value, onCommit, sx }) => {
  const serialize = (v: any) => JSON.stringify(v ?? null, null, 2);
  const [text, setText] = useState<string>(() => serialize(value));
  const [error, setError] = useState<string | null>(null);
  // Tracks the serialized form of what we last emitted (or last synced from the
  // parent), so an external change re-syncs the buffer but our own edits don't.
  const lastSynced = useRef<string>(serialize(value));

  useEffect(() => {
    const incoming = serialize(value);
    if (incoming !== lastSynced.current) {
      setText(incoming);
      lastSynced.current = incoming;
      setError(null);
    }
  }, [value]);

  const handleText = (next: string) => {
    setText(next);
    try {
      const parsed = JSON.parse(next);
      setError(null);
      lastSynced.current = serialize(parsed);
      onCommit(parsed);
    } catch (e: any) {
      setError(e?.message || "Invalid JSON");
    }
  };

  return (
    <TextField
      fullWidth
      multiline
      minRows={6}
      maxRows={20}
      label={label}
      value={text}
      error={Boolean(error)}
      helperText={error || "Changes apply only when the JSON is valid."}
      onChange={(event) => handleText(event.target.value)}
      InputProps={{ sx: { fontFamily: "Menlo, Monaco, Consolas, monospace", fontSize: 13 } }}
      sx={sx}
    />
  );
};

const ClientProcessForm: React.FC<ClientProcessFormProps> = ({ config, handleChange, isMobile = false, syndicationConfig }) => {
  const theme = useTheme();
  const styles = getFormSectionStyles(theme);
  const [tabIndex, setTabIndex] = useState(0);

  const sections = isMobile ? mobileTabSections : tabSections;
  const layouts = isMobile ? mobileTabLayouts : tabLayouts;

  // Guard against a stale tab index if the section list shrinks (odroid -> mobile).
  const safeTabIndex = Math.min(tabIndex, sections.length - 1);
  const activeTab = sections[safeTabIndex];
  const layout = useMemo<FieldGroup[]>(() => layouts[activeTab] ?? [], [layouts, activeTab]);

  const renderScalarField = (fieldKey: string, value: any) => {
    const type = typeof value;
    const label = humanizeFieldKey(fieldKey);
    const readOnlyHelper = READ_ONLY_FIELDS[fieldKey];

    if (type === "number" || type === "string" || value === null || value === undefined) {
      const numeric = isNumericField(fieldKey, value);
      return (
        <TextField
          key={fieldKey}
          fullWidth
          label={label}
          value={value ?? ""}
          type="text"
          disabled={Boolean(readOnlyHelper)}
          helperText={readOnlyHelper}
          inputProps={numeric ? { inputMode: "numeric", pattern: "[0-9]*" } : undefined}
          onChange={(event) => handleChange(fieldKey, event.target.value)}
          sx={styles.inputField}
        />
      );
    }

    // Nested object / array — editable as JSON (buffered, commit-on-valid).
    return (
      <JsonObjectField
        key={fieldKey}
        label={label}
        value={value}
        onCommit={(parsed) => handleChange(fieldKey, parsed)}
        sx={styles.inputField}
      />
    );
  };

  const renderToggleRow = (fieldKey: string, value: boolean) => {
    const label = humanizeFieldKey(fieldKey);
    return (
      <Box key={fieldKey} sx={styles.toggleRow}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
            {label}
          </Typography>
        </Box>
        <FormControlLabel
          sx={{ m: 0 }}
          label=""
          control={
            <Switch
              checked={Boolean(value)}
              onChange={(event) => handleChange(fieldKey, event.target.checked)}
              sx={styles.switchControl}
            />
          }
        />
      </Box>
    );
  };

  const renderGroup = (group: FieldGroup, groupIndex: number) => {
    const groupFields = group.fields
      .map((fieldKey) => ({ fieldKey, value: readPath(config, fieldKey) }))
      .filter(({ value }) => value !== undefined || true); // keep undefined values; treat as empty

    const scalars = groupFields.filter(({ value }) => typeof value !== "boolean");
    const toggles = groupFields.filter(({ value }) => typeof value === "boolean");

    return (
      <Stack key={`${group.title ?? "block"}-${groupIndex}`} spacing={2}>
        {(group.title || group.description) && (
          <Stack spacing={0.5}>
            {group.title && <Typography sx={styles.eyebrow}>{group.title}</Typography>}
            {group.description && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {group.description}
              </Typography>
            )}
          </Stack>
        )}

        {scalars.length > 0 && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
              gap: 2,
            }}
          >
            {scalars.map(({ fieldKey, value }) => (
              <Box
                key={fieldKey}
                sx={{
                  // Multi-line JSON fields take the full row width.
                  gridColumn:
                    value !== null &&
                    value !== undefined &&
                    typeof value !== "string" &&
                    typeof value !== "number"
                      ? { xs: "1 / -1", sm: "1 / -1" }
                      : undefined,
                }}
              >
                {renderScalarField(fieldKey, value)}
              </Box>
            ))}
          </Box>
        )}

        {toggles.length > 0 && (
          <Stack spacing={1}>
            {toggles.map(({ fieldKey, value }) => renderToggleRow(fieldKey, Boolean(value)))}
          </Stack>
        )}
      </Stack>
    );
  };

  return (
    <Box sx={{ ...styles.surfaceCard, p: { xs: 2.5, md: 3.5 } }}>
      {/* Segmented tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={safeTabIndex}
          onChange={(_, next) => setTabIndex(next)}
          variant="scrollable"
          scrollButtons="auto"
          sx={styles.segmentedTabs}
        >
          {sections.map((label) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>
      </Box>

      {/* Tab header */}
      <Stack spacing={0.5} sx={{ mb: 3 }}>
        <Typography sx={styles.eyebrow}>Section</Typography>
        <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: "-0.005em" }}>
          {activeTab}
        </Typography>
        {TAB_DESCRIPTIONS[activeTab] && (
          <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 720 }}>
            {TAB_DESCRIPTIONS[activeTab]}
          </Typography>
        )}
      </Stack>

      {/* Field groups (Wizard uses a dedicated drag-and-drop editor instead of
          raw JSON, which was error-prone to hand-edit). */}
      {activeTab === "Wizard" ? (
        <WizardModuleSelector
          enabled={config.enabled}
          disabled={config.disabled}
          // Mobile units capture manually — External/Pano are odroid-only and
          // are hidden from both lists entirely.
          hiddenModules={isMobile ? ["WizardExternal", "WizardPano"] : undefined}
          onChange={(enabledMap, disabledMap) => {
            handleChange("enabled", enabledMap);
            handleChange("disabled", disabledMap);
          }}
        />
      ) : activeTab === "Iframe" ? (
        <IframeOrderingTab
          config={config}
          handleChange={handleChange}
          syndicationConfig={syndicationConfig}
        />
      ) : (
        <Stack spacing={3.5} divider={<Divider sx={{ borderColor: styles.surfaceDivider }} />}>
          {layout.map((group, index) => renderGroup(group, index))}
        </Stack>
      )}
    </Box>
  );
};

export default ClientProcessForm;
